import React, { useState, useMemo } from 'react';
import { Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { EventTile, EventTileProps } from '../EventTile';
import type { MockReasoning, MockEntity, LegalReference } from '../../types';
import './EventFeed.css';

export type TimeGroup = 'now' | 'next-month' | 'this-season' | 'earlier';

export interface EventData extends Omit<EventTileProps, 'isExpanded' | 'onToggle' | 'isEvenRow'> {
  /** Used for grouping - when the event should surface */
  relevantDate: Date | string;
}

/**
 * Extended event data for Control Assistant with reasoning support.
 * Use this for events that need drill-down capability.
 */
export interface ControlEventData extends EventData {
  /** Reasoning behind this event (for drill-down) */
  reasoning?: MockReasoning;

  /** Entities involved in this event */
  entities?: MockEntity[];

  /** Legal references map for this event */
  legalRefMap?: Record<string, LegalReference>;

  /** Category tags for lense filtering */
  categoryTags?: string[];

  /** Inspection likelihood level (for inspection-likelihood events) */
  likelihoodLevel?: 'niedrig' | 'mittel' | 'hoch';

  /** Whether this is a success/ready state */
  isReady?: boolean;
}

export interface EventFeedProps {
  /** Events to display */
  events: EventData[];
  /** Optional class name */
  className?: string;
  /** Empty state message */
  emptyMessage?: string;
  /** Custom time group labels */
  groupLabels?: Partial<Record<TimeGroup, string>>;
}

/** Default labels for time groups */
const DEFAULT_GROUP_LABELS: Record<TimeGroup, string> = {
  'now': 'Now',
  'next-month': 'Next month',
  'this-season': 'This season',
  'earlier': 'Earlier',
};

/** Determine which time group an event belongs to based on relevantDate and event type */
function getTimeGroup(relevantDate: Date | string, eventType?: string): TimeGroup {
  const date = typeof relevantDate === 'string' ? new Date(relevantDate) : relevantDate;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffMs = eventDay.getTime() - today.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  // Historical/resolved events with past dates go to "earlier"
  if (diffDays < 0 && eventType === 'situation-resolved') {
    return 'earlier';
  }

  // Current/overdue items (today or past) go to "now"
  if (diffDays <= 0) {
    return 'now';
  }

  // Events in the next 30 days go to "next-month"
  if (diffDays <= 30) {
    return 'next-month';
  }

  // Everything else goes to "this-season"
  return 'this-season';
}

/** Group events by time period */
function groupEventsByTime(events: EventData[]): Record<TimeGroup, EventData[]> {
  const groups: Record<TimeGroup, EventData[]> = {
    'now': [],
    'next-month': [],
    'this-season': [],
    'earlier': [],
  };

  events.forEach((event) => {
    const group = getTimeGroup(event.relevantDate, event.type);
    groups[group].push(event);
  });

  // Sort events within each group by relevantDate (most relevant first)
  Object.keys(groups).forEach((key) => {
    const group = key as TimeGroup;
    groups[group].sort((a, b) => {
      const dateA = typeof a.relevantDate === 'string' ? new Date(a.relevantDate) : a.relevantDate;
      const dateB = typeof b.relevantDate === 'string' ? new Date(b.relevantDate) : b.relevantDate;
      // For earlier, sort descending (most recent first)
      // For future, sort ascending (soonest first)
      if (group === 'earlier') {
        return dateB.getTime() - dateA.getTime();
      }
      return dateA.getTime() - dateB.getTime();
    });
  });

  return groups;
}

/** Time group header component */
interface TimeGroupHeaderProps {
  group: TimeGroup;
  label: string;
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}

function TimeGroupHeader({ label, collapsible, collapsed, onToggle }: TimeGroupHeaderProps) {
  const handleClick = () => {
    if (collapsible && onToggle) {
      onToggle();
    }
  };

  return (
    <div
      className={`event-feed__group-header ${collapsible ? 'event-feed__group-header--collapsible' : ''}`}
      onClick={handleClick}
      role={collapsible ? 'button' : undefined}
      tabIndex={collapsible ? 0 : undefined}
      onKeyDown={collapsible ? (e) => e.key === 'Enter' && onToggle?.() : undefined}
    >
      {collapsible && (
        <span className="event-feed__group-chevron">
          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        </span>
      )}
      <span className="event-feed__group-label">{label}</span>
      <span className="event-feed__group-line" />
    </div>
  );
}

/**
 * EventFeed - A time-grouped list of events for the Workspace section.
 * Groups events by: Now (current/overdue), Next Month, This Season, Earlier (historical)
 */
export const EventFeed: React.FC<EventFeedProps> = ({
  events,
  className = '',
  emptyMessage = 'No events to display',
  groupLabels = {},
}) => {
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<TimeGroup>>(new Set(['earlier']));

  const labels = { ...DEFAULT_GROUP_LABELS, ...groupLabels };

  const groupedEvents = useMemo(() => groupEventsByTime(events), [events]);

  // Order of groups to display
  const groupOrder: TimeGroup[] = ['now', 'next-month', 'this-season', 'earlier'];

  // Groups that can be collapsed
  const collapsibleGroups: Set<TimeGroup> = new Set(['earlier']);

  const toggleGroupCollapse = (group: TimeGroup) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };

  // Track global index for alternating row colors
  let globalIndex = 0;

  const hasEvents = events.length > 0;

  return (
    <div className={`event-feed ${className}`}>
      {hasEvents ? (
        groupOrder.map((group) => {
          const groupEvents = groupedEvents[group];
          if (groupEvents.length === 0) return null;

          const isCollapsible = collapsibleGroups.has(group);
          const isCollapsed = collapsedGroups.has(group);

          return (
            <div key={group} className="event-feed__group">
              <TimeGroupHeader
                group={group}
                label={labels[group]}
                collapsible={isCollapsible}
                collapsed={isCollapsed}
                onToggle={() => toggleGroupCollapse(group)}
              />
              {!isCollapsed && (
                <div className="event-feed__events">
                  {groupEvents.map((event) => {
                    const isEven = globalIndex % 2 === 0;
                    globalIndex++;

                    return (
                      <EventTile
                        key={event.id}
                        {...event}
                        isExpanded={expandedEventId === event.id}
                        onToggle={() =>
                          setExpandedEventId(expandedEventId === event.id ? null : event.id)
                        }
                        isEvenRow={isEven}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      ) : (
        <div className="event-feed__empty">
          <Calendar className="event-feed__empty-icon" />
          <p className="event-feed__empty-message">{emptyMessage}</p>
        </div>
      )}
    </div>
  );
};

export default EventFeed;
